import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from "@dnd-kit/core";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { format, isSameDay, startOfDay, subDays, addDays, eachDayOfInterval } from "date-fns";
import { Location, Order, WorkOrder, DriverDayOff } from "@/types/tms";
import { getLocationDisplayName } from "@/lib/location-utils";
import { cn } from "@/lib/utils";

import { toast } from "sonner";
import { OrderWithRelations, DragData } from "@/components/schedule/types";
import { ScheduleTableRow } from "@/components/schedule/ScheduleTableRow";
import { TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { OrderEditDialog } from "@/components/orders/OrderEditDialog";
import { OrderDetailSheet } from "@/components/orders/OrderDetailSheet";
import { useNavigate } from "react-router-dom";
import { useFleetSchedulerAssignment } from "@/hooks/useFleetSchedulerAssignment";
import { useTurnTime } from "@/hooks/useTurnTime";
import { TractorWithHome, DriverWithHome } from "@/types/resources";
import { ScheduleHeader } from "@/components/schedule/ScheduleHeader";
import {
  ORDERS_WITH_LEGS_SELECT,
  ORDERS_WITH_LEGS_VIEW,
  mapAndHydrateLegRows,
} from "@/lib/orders-with-legs";
import { useStarredOrderIds } from "@/hooks/useOrderStars";

interface OrderBoardViewProps {
  searchQuery?: string;
  statusFilter?: string | string[];
  shipFromFilter?: string | string[];
  shipToFilter?: string | string[];
  dateFilter?: { from: Date; to: Date } | null;
  tenderFilter?: string;
  bolFilter?: string;
  tenderIdFilter?: string;
  trailerTypeFilter?: string | string[];
  trailerLoadStatusFilter?: string | string[];
  apiCreatedFilter?: string;
  tripTypeFilter?: string | string[];
  orderTypeFilter?: string;
  locations?: Location[];
  subscribedOnly?: boolean;
}

const DAYS_PAST = 2;
const DAYS_FUTURE = 12;

export function OrderBoardView({
  searchQuery = "",
  statusFilter = "all",
  shipFromFilter = "all",
  shipToFilter = "all",
  dateFilter,
  tenderFilter = "all",
  bolFilter = "all",
  tenderIdFilter = "all",
  trailerTypeFilter = "all",
  trailerLoadStatusFilter = "all",
  apiCreatedFilter = "all",
  tripTypeFilter = "all",
  orderTypeFilter = "all",
  locations: locationsProp,
  subscribedOnly = false,
}: OrderBoardViewProps) {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenant();
  const navigate = useNavigate();

  const selectedStatuses = (Array.isArray(statusFilter) ? statusFilter : [statusFilter]).filter((v) => v && v !== "all");
  const selectedTripTypes = (Array.isArray(tripTypeFilter) ? tripTypeFilter : [tripTypeFilter]).filter((v) => v && v !== "all");
  const selectedTrailerTypeIds = (Array.isArray(trailerTypeFilter) ? trailerTypeFilter : [trailerTypeFilter]).filter((v) => v && v !== "all");
  const selectedTrailerLoadStatuses = (Array.isArray(trailerLoadStatusFilter) ? trailerLoadStatusFilter : [trailerLoadStatusFilter]).filter((v) => v && v !== "all");

  const { data: starredOrderIds } = useStarredOrderIds();

  // Render exactly the date range selected in the shared filter (parity with List view).
  const { weekStart, weekEnd, weekDays } = useMemo(() => {
    const fromDate = dateFilter?.from ?? new Date();
    const toDate = dateFilter?.to ?? fromDate;
    const start = startOfDay(fromDate);
    const lastDay = startOfDay(toDate);
    const end = addDays(lastDay, 1); // exclusive upper bound for queries
    const days = eachDayOfInterval({ start, end: lastDay });
    return { weekStart: start, weekEnd: end, weekDays: days };
  }, [dateFilter]);

  const { turnTimeMinutes, handleTurnTimeChange } = useTurnTime();
  const [activeDragData, setActiveDragData] = useState<DragData | null>(null);
  const [editOrder, setEditOrder] = useState<Order | null>(null);
  const [previewOrder, setPreviewOrder] = useState<OrderWithRelations | null>(null);

  // Weekly assignment hook
  const { assignPlus: assign, isAssigningPlus: isAssigning, clear, isClearing } = useFleetSchedulerAssignment(weekStart, turnTimeMinutes);

  const { data: locationsFetched } = useQuery({
    queryKey: ["locations"],
    enabled: !locationsProp,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("*")
        .order("short_code");
      if (error) throw error;
      return data as Location[];
    },
  });
  const locations = locationsProp ?? locationsFetched;

  const { data: tractors } = useQuery({
    queryKey: ["tractors-with-home-base"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tractors")
        .select(`
          *,
          home_base:locations(*)
        `)
        .order("name");
      if (error) throw error;
      return data as TractorWithHome[];
    },
  });

  const { data: drivers } = useQuery({
    queryKey: ["drivers-with-flights"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("drivers")
        .select(`
          *,
          home_base:locations(*),
          flight:flights(*)
        `)
        .order("name");
      if (error) throw error;
      return data as DriverWithHome[];
    },
  });

  const { data: workOrders } = useQuery({
    queryKey: ["work-orders", weekStart.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work_orders")
        .select("*")
        .gte("start_time", weekStart.toISOString())
        .lt("start_time", weekEnd.toISOString())
        .order("start_time");
      if (error) throw error;
      return data as WorkOrder[];
    },
  });

  const { data: daysOff } = useQuery({
    queryKey: ["driver-days-off", weekStart.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("driver_days_off")
        .select("*")
        .gte("date", format(weekStart, "yyyy-MM-dd"))
        .lt("date", format(weekEnd, "yyyy-MM-dd"));
      if (error) throw error;
      return data as DriverDayOff[];
    },
  });

  const { data: orders } = useQuery({
    queryKey: ["schedule-orders", weekStart.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDERS_WITH_LEGS_SELECT)
        .gte("ready_time", weekStart.toISOString())
        .lt("ready_time", weekEnd.toISOString())
        .order("ready_time")
        .limit(3000);
      if (error) throw error;
      return mapAndHydrateLegRows(data);
    },
  });

  const updateOrderMutation = useMutation({
    mutationFn: async ({
      orderId,
      updates,
      resourceName,
      resourceType,
    }: {
      orderId: string;
      updates: { assigned_operator_id?: string; assigned_tractor_id?: string };
      resourceName: string;
      resourceType: "driver" | "tractor";
    }) => {
      await supabase.rpc("set_tenant_slug", { _slug: activeTenant });
      // `orderId` may now be a leg id (cards are per-leg). Resolve to the parent order id
      // before writing assignment fields, which still live on the parent `orders` row.
      const targetOrderId =
        orders?.find((o) => o.id === orderId)?.parent_order_id ?? orderId;
      const { data: currentOrder } = await supabase
        .from("orders")
        .select("assigned_operator_id, assigned_tractor_id, assigned_operator:drivers!assigned_operator_id(name), assigned_tractor:tractors(name)")
        .eq("id", targetOrderId)
        .single();

      const { error } = await supabase.from("orders").update(updates).eq("id", targetOrderId);
      if (error) throw error;

      const fieldName = resourceType === "driver" ? "assigned_operator_id" : "assigned_tractor_id";
      const oldValue = resourceType === "driver"
        ? (currentOrder?.assigned_operator as { name: string } | null)?.name || null
        : (currentOrder?.assigned_tractor as { name: string } | null)?.name || null;

      const { error: logError } = await supabase.from("order_change_log").insert({
        order_id: targetOrderId,
        field_name: fieldName,
        old_value: oldValue,
        new_value: resourceName,
        changed_by: "System",
      });
      if (logError) console.error("Failed to log change:", logError);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
      toast.success("Order updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // BOL order ids for tenant (only fetched when bolFilter is active)
  const { data: bolOrderIds } = useQuery({
    queryKey: ["board-bol-order-ids", activeTenant],
    enabled: bolFilter !== "all" && !!activeTenant,
    queryFn: async () => {
      const { data } = await supabase
        .from("order_bol_images")
        .select("order_id")
        .eq("tenant_id", activeTenant as string);
      return new Set((data ?? []).map((b) => b.order_id));
    },
  });

  // Load Tender (document) order ids for tenant.
  const { data: tenderOrderIds } = useQuery({
    queryKey: ["board-tender-order-ids", activeTenant],
    enabled: tenderFilter !== "all" && !!activeTenant,
    queryFn: async () => {
      const { data } = await supabase
        .from("order_tender_images")
        .select("order_id")
        .eq("tenant_id", activeTenant as string);
      return new Set((data ?? []).map((t) => t.order_id));
    },
  });

  // Trailer ids (text) whose trailers row has a matching load_status.
  const { data: trailerLoadStatusTrailerIds } = useQuery({
    queryKey: ["board-trailer-load-status", activeTenant, selectedTrailerLoadStatuses.slice().sort().join(",")],
    enabled: selectedTrailerLoadStatuses.length > 0 && !!activeTenant,
    queryFn: async () => {
      const { data } = await supabase
        .from("trailers")
        .select("trailer_id, load_status")
        .eq("tenant_id", activeTenant as string)
        .in("load_status", selectedTrailerLoadStatuses as any);
      return new Set(((data ?? []).map((t) => t.trailer_id).filter(Boolean) as string[]));
    },
  });

  // Compute multi-leg parent ids from current orders set (one row per leg).
  const multiLegParentIds = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of orders ?? []) {
      const pid = (o as any).parent_order_id ?? o.id;
      if (!pid) continue;
      counts.set(pid, (counts.get(pid) ?? 0) + 1);
    }
    const set = new Set<string>();
    for (const [pid, c] of counts) if (c > 1) set.add(pid);
    return set;
  }, [orders]);

  const matchLocationFilter = (
    ids: string[],
    addr: string | null | undefined,
    city: string | null | undefined,
  ) => {
    if (ids.length === 0) return true;
    const selected = (locations ?? []).filter((l) => ids.includes(l.id));
    if (selected.length === 0) return false;
    return selected.some(
      (l) => (l.street_address ?? "") === (addr ?? "") && (l.city ?? "") === (city ?? ""),
    );
  };

  const shipFromIds = (Array.isArray(shipFromFilter) ? shipFromFilter : [shipFromFilter]).filter(
    (v) => v && v !== "all",
  ) as string[];
  const shipToIds = (Array.isArray(shipToFilter) ? shipToFilter : [shipToFilter]).filter(
    (v) => v && v !== "all",
  ) as string[];

  // Filter orders based on all active filters (parity with List view).
  const filteredOrders = useMemo(() => {
    if (!orders) return [];

    return orders.filter((order: any) => {
      // Status filter
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(order.status)) {
        return false;
      }

      // Subscribed-orders filter — match by parent id so legs of a starred
      // multi-leg order remain visible.
      if (subscribedOnly) {
        const pid = order.parent_order_id ?? order.id;
        if (!starredOrderIds?.has(pid)) return false;
      }

      // Tender ID filter (tender_id text column)
      if (tenderIdFilter === "has" && !order.tender_id) return false;
      if (tenderIdFilter === "missing" && order.tender_id) return false;

      // Load Tender (document) filter
      if (tenderFilter !== "all") {
        const pid = order.parent_order_id ?? order.id;
        const has = tenderOrderIds?.has(pid) ?? false;
        if (tenderFilter === "has" && !has) return false;
        if (tenderFilter === "missing" && has) return false;
      }

      // Trailer Type filter
      if (selectedTrailerTypeIds.length > 0) {
        if (!order.trailer_type_id || !selectedTrailerTypeIds.includes(order.trailer_type_id)) return false;
      }

      // Trailer Load Status filter
      if (selectedTrailerLoadStatuses.length > 0) {
        if (!order.trailer_id || !trailerLoadStatusTrailerIds?.has(order.trailer_id)) return false;
      }

      // API-created filter
      if (apiCreatedFilter === "api" && !order.is_api_created) return false;
      if (apiCreatedFilter === "manual" && order.is_api_created) return false;

      // Trip type filter
      if (selectedTripTypes.length > 0 && !selectedTripTypes.includes(order.trip_type)) return false;

      // Order type filter (single/multi leg)
      if (orderTypeFilter === "multi_leg" || orderTypeFilter === "single_leg") {
        const pid = order.parent_order_id ?? order.id;
        const isMulti = multiLegParentIds.has(pid);
        if (orderTypeFilter === "multi_leg" && !isMulti) return false;
        if (orderTypeFilter === "single_leg" && isMulti) return false;
      }

      // BOL filter
      if (bolFilter !== "all") {
        const pid = order.parent_order_id ?? order.id;
        const has = bolOrderIds?.has(pid) ?? false;
        if (bolFilter === "has" && !has) return false;
        if (bolFilter === "missing" && has) return false;
      }

      // Ship-to / ship-from address+city match
      if (!matchLocationFilter(shipFromIds, order.ship_from_street_address, order.ship_from_city))
        return false;
      if (!matchLocationFilter(shipToIds, order.ship_to_street_address, order.ship_to_city))
        return false;

      // Search filter
      if (searchQuery.trim()) {
        const searchLower = searchQuery.trim().toLowerCase();
        const matchesSearch = 
          order.unique_id.toLowerCase().includes(searchLower) ||
          order.ship_from_city.toLowerCase().includes(searchLower) ||
          order.ship_to_city.toLowerCase().includes(searchLower) ||
          (order.trailer_id?.toLowerCase().includes(searchLower) || false) ||
          (order.tender_id?.toLowerCase().includes(searchLower) || false) ||
          (order.billing_party?.organization_name?.toLowerCase().includes(searchLower) || false) ||
          (order.assigned_operator?.name?.toLowerCase().includes(searchLower) || false) ||
          (order.assigned_tractor?.name?.toLowerCase().includes(searchLower) || false);
        if (!matchesSearch) return false;
      }
      
      return true;
    });
  }, [
    orders,
    selectedStatuses.join(","),
    searchQuery,
    tenderFilter,
    bolFilter,
    tenderIdFilter,
    selectedTrailerTypeIds.join(","),
    selectedTrailerLoadStatuses.join(","),
    apiCreatedFilter,
    tenderOrderIds,
    trailerLoadStatusTrailerIds,
    selectedTripTypes.join(","),
    orderTypeFilter,
    bolOrderIds,
    multiLegParentIds,
    shipFromIds.join(","),
    shipToIds.join(","),
    locations,
    subscribedOnly,
    starredOrderIds,
  ]);

  const getOrdersForLocation = (locationName: string) =>
    filteredOrders.filter((order) => order.ship_from_city === locationName);

  // Orders whose ship_from_city doesn't match any configured location row.
  // Without this, they'd be silently dropped from the Board (parity bug vs. List view).
  const unmatchedOrdersByCity = useMemo(() => {
    if (!locations) return new Map<string, OrderWithRelations[]>();
    const matchableCities = new Set(
      locations.map((l) => l.city || l.short_code).filter(Boolean),
    );
    const groups = new Map<string, OrderWithRelations[]>();
    for (const order of filteredOrders) {
      const city = order.ship_from_city;
      if (!city || matchableCities.has(city)) continue;
      const key = city;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(order);
    }
    return groups;
  }, [filteredOrders, locations]);

  // Calculate counts for button display
  const unassignedCount = orders?.filter(
    (o) => !o.assigned_tractor_id && o.status !== "canceled" && !o.is_work_order
  ).length || 0;

  const assignedCount = orders?.filter(
    (o) => o.assigned_tractor_id && !o.is_work_order
  ).length || 0;

  const handleWeeklyAssign = () => {
    if (!orders || !tractors || !drivers) return;
    assign({ orders, tractors, drivers, workOrders: workOrders || [], daysOff: daysOff || [] });
  };

  const handleWeeklyClear = () => {
    if (!orders || !tractors || !drivers) return;
    if (confirm(`Clear assignments from ${assignedCount} orders?`)) {
      clear({ orders, tractors, drivers });
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragData(event.active.data.current as DragData);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragData(null);
    const { active, over } = event;

    if (!over || !active.data.current) return;

    const dragData = active.data.current as DragData;
    const dropData = over.data.current as { orderId: string; locationId: string } | undefined;

    if (!dropData?.orderId) return;

    const updates: { assigned_operator_id?: string; assigned_tractor_id?: string } = {};

    if (dragData.type === "driver") {
      updates.assigned_operator_id = dragData.id;
    } else if (dragData.type === "tractor") {
      updates.assigned_tractor_id = dragData.id;
    }

    updateOrderMutation.mutate({
      orderId: dropData.orderId,
      updates,
      resourceName: dragData.name,
      resourceType: dragData.type,
    });
  };

  const handleOrderClick = (order: OrderWithRelations) => {
    setPreviewOrder(order);
  };

  // Filter locations based on ship-from filter
  const filteredLocations = useMemo(() => {
    if (!locations) return [];
    const ids = Array.isArray(shipFromFilter) ? shipFromFilter : [shipFromFilter];
    const selected = ids.filter((v) => v && v !== "all");
    if (selected.length === 0) return locations;
    return locations.filter((l) => selected.includes(l.id));
  }, [locations, shipFromFilter]);

  return (
    <>
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {/* Schedule Table */}
        <div className="w-full min-w-0 max-w-full overflow-hidden border rounded-lg bg-card h-[calc(100vh-180px)]">
          <div className="w-full max-w-full h-full overflow-x-auto overflow-y-auto">
            <table className="w-full min-w-full caption-bottom text-sm">
            <TableHeader>
              <TableRow className="bg-card border-b">
                <TableHead className="w-[200px] min-w-[200px] max-w-[200px] sticky top-0 left-0 z-30 bg-popover border-b">
                  Ship From Location
                </TableHead>
                {weekDays.map((day) => (
                  <TableHead
                    key={day.toISOString()}
                    className={cn(
                      "text-center min-w-[180px] sticky top-0 z-20 bg-card border-b",
                      isSameDay(day, new Date()) && "bg-primary/5"
                    )}
                  >
                    <div className="text-xs text-muted-foreground text-center">{format(day, "EEE")}</div>
                    <div
                      className={cn(
                        "text-lg font-medium text-center",
                        isSameDay(day, new Date()) && "text-primary"
                      )}
                    >
                      {format(day, "d")}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLocations.length > 0 ? (
                filteredLocations.map((location) => {
                  const locationOrders = getOrdersForLocation(location.city || location.short_code);
                  return (
                    <ScheduleTableRow
                      key={location.id}
                      locationId={location.id}
                      locationName={getLocationDisplayName(location)}
                      orders={locationOrders}
                      weekDays={weekDays}
                      onOrderClick={handleOrderClick}
                      locations={locations}
                    />
                  );
                })
              ) : (
                <TableRow>
                  <td colSpan={8} className="p-8 text-center text-muted-foreground">
                    No locations configured
                  </td>
                </TableRow>
              )}
              {/* Unmatched ship-from cities — only when no ship-from filter is active */}
              {shipFromIds.length === 0 &&
                Array.from(unmatchedOrdersByCity.entries())
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([city, cityOrders]) => (
                    <ScheduleTableRow
                      key={`unmatched-${city}`}
                      locationId={`unmatched-${city}`}
                      locationName={city}
                      orders={cityOrders}
                      weekDays={weekDays}
                      onOrderClick={handleOrderClick}
                      locations={locations}
                    />
                  ))}
            </TableBody>
            </table>
          </div>
        </div>

        {/* Drag overlay */}
        <DragOverlay>
          {activeDragData && (
            <div className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground shadow-lg">
              {activeDragData.type === "driver" ? "🧑‍✈️" : "🚛"} {activeDragData.name}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Order Edit Dialog */}
      <OrderEditDialog
        order={editOrder}
        open={!!editOrder}
        onOpenChange={(open) => { if (!open) setEditOrder(null); }}
      />

      {/* Order Preview Sheet */}
      <OrderDetailSheet
        order={previewOrder}
        open={!!previewOrder}
        onOpenChange={(open) => { if (!open) setPreviewOrder(null); }}
        onEdit={(order) => {
          setPreviewOrder(null);
          setEditOrder(order as Order);
        }}
        onView={(order) => {
          const targetId = (order as any).parent_order_id || order.id;
          const legParam = (order as any).parent_order_id ? `?leg=${order.id}` : "";
          navigate(`/orders/${targetId}${legParam}`);
        }}
      />
    </>
  );
}
